<template>
  <v-card class="session__container">
    <ChooseLanguage :positionAbsolute="true" />
    <div class="session__content">
      <div>
        <v-img
          src="@/static/brand_logo.svg"
          class="mx-auto session__logo"
          aspect-ratio
          width="300"
        >
        </v-img>
      </div>
      <h2 class="text-center mb-3 session__title">
        {{ $t("message.common.login") }}
      </h2>
      <p class="text-center mb-5 session__description">
        {{ $t("message.session.login.description") }}
      </p>
      <v-form v-model="valid" @keyup.native.enter="submit" class="mb-4">
        <v-text-field
          class="input-field"
          :label="emailLabel"
          v-model="email"
          color="primary"
          v-validate="'required|email'"
          :error-messages="errors.collect(emailLabel)"
          :data-vv-name="emailLabel"
          required
        ></v-text-field>
        <v-text-field
          class="input-field"
          :label="passwordLabel"
          v-model="password"
          type="password"
          color="primary"
          v-validate="'required'"
          :error-messages="errors.collect(passwordLabel)"
          :data-vv-name="passwordLabel"
          required
        ></v-text-field>

        <!-- TODO: Remember me methods -->
        <!-- <v-checkbox
          color="secondary"
          class="session__checkbox"
          :label="this.$t('message.common.rememberMe')"
          value="1"
          dense
          v-model="rememberMe"
        ></v-checkbox> -->

        <div>
          <vue-recaptcha
            ref="recaptcha"
            :sitekey="reCaptchaSiteKey"
            :loadRecaptchaScript="true"
            badge="bottomleft"
            @verify="onCaptchaVerified"
            @expired="onCaptchaExpired"
          >
            <v-btn large block :loading="loading" color="primary" class="mb-4">
              {{ $t("message.common.login") }}
            </v-btn>
          </vue-recaptcha>
        </div>
        <router-link
          v-if="!isDemoMode"
          class="d-block mb-4 text-right"
          :to="{ name: 'session-forgot-password' }"
        >
          {{ $t("message.common.forgotPassword") }}
        </router-link>
        <p v-if="!isDemoMode" class="text-center">
          {{ $t("message.session.dontHaveAccount") }}
          <router-link :to="{ name: 'session-select-account-type' }">
            {{ $t("message.common.signUp") }}
          </router-link>
        </p>
        <!--<div class="mb-12">-->
        <!--<v-btn large block dark :color="facebookColor" class="mb-3">-->
        <!--<v-icon left>mdi-facebook</v-icon>-->
        <!--{{ $t("message.session.loginWithFacebook") }}-->
        <!--</v-btn>-->
        <!--<v-btn large block dark color="darkGreen" class="mb-3">-->
        <!--<v-icon left>icon-google</v-icon>-->
        <!--<i class="google-icon"></i>{{ $t("message.session.loginWithGoogle") }}-->
        <!--</v-btn>-->
        <!--</div>-->
      </v-form>
    </div>
    <SectionLoader
      v-if="showOverlayLoader"
      class="session__overlay-loader"
      :status="showOverlayLoader"
      :opacity="1"
    />
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import constants from "@/config/constants.js";
import ChooseLanguage from "@/components/AppBar/ChooseLanguage.vue";
import VueRecaptcha from "vue-recaptcha";
import { googleApiConfig } from "@/config/google-api";
import { getDemoCredentials } from "@/utils/demo-credentials.js";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";

export default {
  name: "SessionLogin",
  data() {
    return {
      showOverlayLoader: false,
      loading: false,
      passwordLabel: this.$t("message.common.password"),
      emailLabel: this.$t("message.common.email"),
      // rememberMe: false,
      valid: false,
      email: "",
      password: "",
      appBrand: constants.appBrand,
      facebookColor: constants.facebookColor,
      googleColor: constants.googleColor,
      reCaptchaSiteKey: null
    };
  },
  components: {
    ChooseLanguage,
    VueRecaptcha,
    SectionLoader
  },
  computed: {
    ...mapGetters({
      isDemoMode: "auth/isDemoMode"
    }),
    selectedLocale() {
      return this.$store.state.settings.selectedLocale;
    }
  },
  async created() {
    this.reCaptchaSiteKey = this.getReCaptchaKey();
    const isAuthenticated = await this.$store.getters["auth/isAuthenticated"];

    if (isAuthenticated) {
      this.$store.dispatch("auth/logout", false);
    }
    await this.handleDemoAutoLogin();
  },
  mounted() {
    document
      .querySelectorAll(".input-field label")
      .forEach(el => el.classList.add("v-label--active"));
  },
  methods: {
    async handleDemoAutoLogin() {
      if (this.isDemoMode && this.$route.query.prefill) {
        if (this.$route.query.email) {
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.DEMO_USER,
            this.$route.query.email
          );
        }
        try {
          this.showOverlayLoader = true;
          this.setDemoCredentials();
          await this.signIn();
        } catch (error) {
          //console.error(error);
        } finally {
          this.showOverlayLoader = false;
        }
      }
    },
    setDemoCredentials() {
      const { email, password } = getDemoCredentials(this.selectedLocale);
      this.email = email;
      this.password = password;
    },

    async submit() {
      await this.$validator.validate().then(async isTrue => {
        isTrue ? await this.signIn() : null;
      });
    },

    getReCaptchaKey() {
      return window.Cypress
        ? googleApiConfig.reCaptchaSiteKeyTesting
        : googleApiConfig.reCaptchaSiteKey;
    },

    async signIn() {
      this.loading = true;

      try {
        await this.$store.dispatch("auth/signInWithEmailAndPassword", {
          email: this.email,
          password: this.password
        });

        if (this.$store.getters["auth/isAuthenticated"]) {
          await this.$router.push(
            (this.$route.params &&
              this.$route.params.redirectAfterLoginPath) || {
              name: "dashboard"
            }
          );
        } else {
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
      }
    },

    onCaptchaVerified() {
      this.$refs.recaptcha.reset();
      this.submit();
    },

    onCaptchaExpired() {
      this.$refs.recaptcha.reset();
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .session__overlay-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
}
</style>
